import { CancellationPolicy } from 'gql/autogen/CancellationPolicy';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { StringFormatter } from '@agoda/react-handyman';
import { PenaltyCode } from 'core-framework/constant/PenaltyCode';

const getHoursUntil = (policy: CancellationPolicy) => {
    return policy.hoursUntil || 0;
};

const getHoursFrom = (policy: CancellationPolicy) => {
    return policy.hoursFrom || 0;
};

const compareCancellationPolicyDesc = (a: CancellationPolicy, b: CancellationPolicy) => {
    const previous = getHoursFrom(a);
    const next = getHoursFrom(b);
    return next - previous;
};

// https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary
const convertHoursToDays = (hours = 0, useFloorValue = false): number => {
    const days = hours / 24;
    return useFloorValue ? Math.floor(days + Number.EPSILON) : Math.round(days + Number.EPSILON);
};

export const sortCancellationPolicies = (unsortedPolicies: CancellationPolicy[] = []) => {
    if (!unsortedPolicies || unsortedPolicies.length === 0) {
        return unsortedPolicies || [];
    }
    const policies = unsortedPolicies.slice().sort(compareCancellationPolicyDesc);
    const policiesWithUniqueNonRefundable = [];
    for (const policy of policies) {
        policiesWithUniqueNonRefundable.push(policy);
        if (policy.penaltyCode?.includes('100')) {
            break;
        }
    }
    return policiesWithUniqueNonRefundable;
};

/**
 * This function defaults to <b>No Cancellation</b> to avoid issues with Cancellation
 */
export const getPenaltyFromCode = (penaltyCode = PenaltyCode.COMPLETE_PENALTY.toString()): number => {
    if (!penaltyCode) return 100;
    return Number(penaltyCode.replace('p', '').replace('P', '').trim() || '100');
};

export const getCancellationPolicyDescriptionForFreeCancellation = (hoursUntil: number, cms: CmsValue) => {
    const days = convertHoursToDays(hoursUntil, true);
    const extraHours = hoursUntil - days * 24;
    if (days < 2) {
        if (hoursUntil === 0) {
            // Cancel by 23:59 (activity local time) the day before the activity start date for a full refund.
            return cms[325937];
        }
        // Risk free until {0} hours prior to activity start date (activity local time)
        return StringFormatter.format(cms[150676], hoursUntil);
    }

    // 150678 - Risk free until {0} days prior to activity start date (activity local time)
    // 150680 - Risk free until {0} days {1} hours prior to activity start date (activity local time)
    return extraHours === 0 ? StringFormatter.format(cms[150678], days) : StringFormatter.format(cms[150680], days, extraHours);
};

export const getCancellationPolicyDescription = (policy: CancellationPolicy, penalty: number, policyCount: number, cms: CmsValue) => {
    const hoursUntil = getHoursUntil(policy);
    const hoursFrom = getHoursFrom(policy);

    if (policyCount === 1 && penalty >= 100) {
        // This booking is Non-Refundable
        return cms[149965];
    }
    if (penalty === 0) {
        return getCancellationPolicyDescriptionForFreeCancellation(hoursUntil, cms);
    }

    const days = convertHoursToDays(hoursFrom, true);
    const extraHours = hoursFrom - days * 24;

    if (hoursUntil > 0) {
        // Up to {0} days before activity start date (activity local time)
        return days < 2 ? StringFormatter.format(cms[149961], hoursFrom) : StringFormatter.format(cms[149960], days);
    }

    if (days < 2) {
        if (hoursFrom === 0) {
            if (penalty === 100) {
                // Non-refundable if you cancel after 00:01 (activity local time) on the activity start date.
                return cms[325938];
            }

            // Get a x% refund if you cancel after 00:01 (activity local time) on the activity start date.
            return StringFormatter.format(cms[325969], penalty);
        }

        // Within {0} hours before activity start date (activity local time)
        return StringFormatter.format(cms[149963], hoursFrom);
    }
    // Within {0} days {1} hours before activity start date (activity local time)
    return extraHours === 0 ? StringFormatter.format(cms[149962], days) : StringFormatter.format(cms[316912], days, extraHours);
};

export const getCancellationPolicyTitle = (penalty: number, cms: CmsValue) => {
    if (penalty === 0) {
        return cms[149958];
    }
    if (penalty === 100) {
        return cms[149964];
    }
    return StringFormatter.format(cms[149959], penalty);
};

export const getCancellationPolicyModalDescription = (hoursFrom: number, cms: CmsValue): { primary: string; secondary: string } => {
    const days = convertHoursToDays(hoursFrom);

    // Cancel up to {0} days before activity start date and you'll pay nothing.
    const primary = days < 2 ? StringFormatter.format(cms[149968], hoursFrom) : StringFormatter.format(cms[149967], days);

    // If you fail to cancel up to {0} days before activity start date (activity local time), no refund will be given
    const secondary = days < 2 ? StringFormatter.format(cms[149970], hoursFrom) : StringFormatter.format(cms[149969], days);
    return { primary, secondary };
};
